import React, { useState } from 'react'
import moment from 'moment'
import { Row, Col, Select, Divider, Form, Button, Typography } from 'antd'
import { useTranslation } from 'next-i18next'
import styles from './CertificateForm.module.scss'
import { createBusinessCertificate } from 'src/features'
import { useAppDispatch, useAppSelector } from 'src/app/hooks'
import {
  CustomSelect, CustomTextInput, CustomCard,
  CustomDatePicker, CustomInputDecimal, CustomTextArea,
  FormLabels
} from 'src/components'

const { Option } = Select
const { Title } = Typography

const CertificateForm = ({ widgetUsers, loadingWidgetUsers }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [form] = Form.useForm()
  const [selectedUser, setSelectedUser] = useState(-1)
  const [userIntegrations, setUserIntegrations] = useState([])
  const { generateCertificate: { loading, response } } = useAppSelector((state) => state.admin)

  const handleUserChange = (userId: number) => {
    setSelectedUser(userId)
    const user = widgetUsers.find((user) => user.id === userId)
    setUserIntegrations(user.integrations)
    form.resetFields(['selectedIntegration'])
  }

  const onFinish = (values: any) => {
    const certificate_date = [undefined, null].includes(values?.certificate_date) ? null : moment(values.certificate_date).format('YYYY-MM-DD')
    const params = { ...values, certificate_date }
    dispatch(createBusinessCertificate(params))
    form.resetFields()
  }

  return (
    <>
      <Form
        name='basic'
        onFinish={onFinish}
        autoComplete='off'
        form={form}
      >
        <CustomCard
          cardTitle={<Title level={5}>{t('admin.certificateTitle')}</Title>}
          loading={loadingWidgetUsers}
        >
          {/* User Selection */}
          <Row>
            <Col xs={24} lg={12}>
              <FormLabels
                title={t('admin.IntegrationUser')}
                subtitle={t('admin.SelectUser')}
              />
            </Col>
            <Col xs={24} lg={12}>
              {/* User Selection */}
              {!loadingWidgetUsers && <>
                <CustomSelect
                  name='widgetUser'
                  required={true}
                  placeholder={t('admin.SelectWidgetUser')}
                  onChange={handleUserChange}
                >
                  {widgetUsers.map(user => (
                    <Option value={user.id} key={user.id}>{user.full_name}</Option>
                  ))}
                </CustomSelect>
              </>}
            </Col>
          </Row>
          <Divider />
          {/* User Selection */}
          <Row>
            <Col xs={24} lg={12}>
              <FormLabels
                title={t('admin.Integration')}
                subtitle={t('admin.SelectIntegration')}
              />
            </Col>
            <Col xs={24} lg={12}>
              {/* Select Integration */}
              <CustomSelect
                placeholder={selectedUser === -1 ? t('admin.NoUserSelected') : t('admin.SelectIntegrationTwo')}
                disabled={selectedUser === -1}
                name='selectedIntegration'
                required={true}
              >
                {userIntegrations.map(integration => (
                  <Option value={integration.id} key={integration.id}>{integration.initials}</Option>
                ))}
              </CustomSelect>
            </Col>
          </Row>
        </CustomCard>

        <div className='mt-4'>
          <CustomCard loading={loadingWidgetUsers}>
            {/* Client Name */}
            <Row>
              <Col xs={24} lg={12}>
                <FormLabels
                  title={t('admin.ClientName')}
                  subtitle={t('admin.ClientNameRequired')}
                />
              </Col>
              <Col xs={24} lg={12}>
                {/* Select Integration */}
                <CustomTextInput
                  name='client_name'
                  required={true}
                  placeholder={t('admin.ClientNamePlaceHolder')}
                />
              </Col>
            </Row>
            {/* Certificate Date */}
            <Row>
              <Col xs={24} lg={12}>
                <FormLabels
                  title={t('admin.CertificateDate')}
                  subtitle={t('admin.CertificateDateLabel')}
                />
              </Col>
              <Col xs={24} lg={12}>
                {/* Date Picker */}
                <CustomDatePicker
                  name='certificate_date'
                  placeholder={t('admin.CertificateDatePlaceholder')}
                />
              </Col>
            </Row>
            {/* Compensation Unit */}
            <Row>
              <Col xs={24} lg={12}>
                <FormLabels
                  title={t('admin.CompensationUnit')}
                  subtitle={'(kg / tCO2 / Mwh)'}
                />
              </Col>
              <Col xs={24} lg={12}>
                {/* Select Contribution Unit */}
                <CustomSelect
                  placeholder={t('admin.CompensationUnitPlaceholder')}
                  name='contributionUnit'
                  required={true}
                >
                  <Option value={'Kg'}>Kg</Option>
                  <Option value={'tCO2'}>tCO2</Option>
                  <Option value={'MWh'}>MWh</Option>
                </CustomSelect>
              </Col>
            </Row>
            {/* Quantity */}
            <Row>
              <Col xs={24} lg={12}>
                <FormLabels
                  title={t('admin.QuantityCompensation')}
                  subtitle={t('admin.QuantityCompensationSubtitle')}
                />
              </Col>
              <Col xs={24} lg={12}>
                <CustomInputDecimal
                  placeholder={t('admin.QuantityCompensationPlaceholder')}
                  name='quantityDonated'
                  required={true}
                />
              </Col>
            </Row>
            <Divider />
            {/* Introduction Text */}
            <Row>
              <Col xs={24} lg={12}>
                <FormLabels
                  title={t('admin.IntroductionText')}
                  subtitle={t('general.Optional')}
                />
              </Col>
              <Col xs={24} lg={12}>
                <CustomTextArea
                  name='introductionText'
                  placeholder={t('admin.IntroductionTextPlaceHolder')}
                />
              </Col>
            </Row>
            {/* Emission Coverage Period */}
            <Row>
              <Col xs={24} lg={12}>
                <FormLabels
                  title={t('admin.EmissionCoverage')}
                  subtitle={t('general.Optional')}
                />
              </Col>
              <Col xs={24} lg={12}>
                <CustomTextArea
                  name='emissionCoverageText'
                  placeholder={t('admin.EmissionCoveragePlaceHolder')}
                />
              </Col>
            </Row>
            <Divider />
            {/* Submit Section */}
            <Row>
              <Col xs={24} lg={12}>
                {response != null && !loading && (
                  <div className='mt-2'>
                    <Title level={5}>
                      {t('admin.GenerateClick')}&nbsp;
                      <a href={response} target='_blank' rel='noopener noreferrer'>
                        {t('general.here')}
                      </a>
                    </Title>
                  </div>
                )}
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item>
                  <Button
                    type='primary'
                    htmlType='submit'
                    size='large'
                    shape='round'
                    className={styles['submit-button']}
                    loading={loading}
                  >
                    {loading ? (
                      <>{t('admin.GeneratingCertificate')}</>
                    ) : (
                      <>{t('admin.certificateSubmitText')}</>
                    )}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </CustomCard>
        </div>
      </Form>
    </>
  )
}

export default CertificateForm
